import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { JMCLink } from "@components/JMCLink/JMCLink";
import { NavigationImpression } from "@components/NavigationImpression/NavigationImpression";
import { sectionData, TableOfContents } from "@jmc/core/src/components/TableOfContents/TableOfContents";
import { TableOfContentsWrapper } from "@jmc/core/src/components/TableOfContents/TableOfContentsWrapper/TableOfContentsWrapper";
import { useHeaderProperties } from "@jmc/core/src/hooks/useHeaderProperties";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { cleanCSSIdentifier } from "@jmc/utils/utils/clean-css-identifier";
import { useLocation } from "@reach/router";
import { CMSBlock, CMSFootnotes, CMSSectionProps } from "@types";
import React, { MutableRefObject, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type PropTypes = {
    content: CMSBlock[];
    footnotes?: CMSFootnotes[];
};

export const JMCTableOfContents = ({ content, footnotes }: PropTypes): JSX.Element => {
    const { t } = useTranslation();
    const locale = useLocale();
    const location = useLocation();
    const headerProps = useHeaderProperties();
    const [isOpen, setIsOpen] = useState(false);

    const buildData = (section: CMSSectionProps, index: number): sectionData => {
        if (section?.show_title) {
            const title = (
                <JMCHtml footnotes={footnotes} color="inherit">
                    {section?.section_title}
                </JMCHtml>
            );

            const id = section?.id || cleanCSSIdentifier(section?.section_title);
            const url = decodeURI(location?.pathname?.replace(locale + "/", ""));
            const link = (
                <JMCLink
                    isAllowedOnMedical
                    external={false}
                    url={url}
                    anchor_id={id}
                    data-test-id={`TableOfContents.Item.title`}
                >
                    <TableOfContents.Item id={id} index={index}>
                        <span data-test-id={`TableOfContents.Item.title`}>
                            <JMCHtml footnotes={footnotes}>{section?.section_title}</JMCHtml>
                        </span>
                    </TableOfContents.Item>
                </JMCLink>
            );

            // if the section has any optimizely audiences applied
            const hasAudiences = !!section.audiences?.length;
            // if the section is by default hidden because of an optional Optimizely audience(s) that is being applied
            const shouldBeHiddenByDefault =
                hasAudiences && !section.audiences.some((audience) => audience.shouldBeShownByDefault); // if no applied audience(s) should be shown by default, then it needs to be hidden by default

            return {
                title: title,
                id: id,
                original_title: section?.section_title,
                link: link,
                audienceIds: hasAudiences ? section.audiences.map((audience) => audience.id).join(" ") : "",
                shouldBeHiddenInToC: shouldBeHiddenByDefault,
            };
        }
    };

    const sections =
        content &&
        content
            .map((block: CMSBlock, index) => {
                return buildData(block?.section, index);
            })
            .filter((item: sectionData) => item?.original_title);
    const tocWrapperRef: MutableRefObject<HTMLDivElement> = useRef(null);

    return (
        <TableOfContentsWrapper
            hasTopBar={headerProps.hasTopBar}
            isCollapsed={headerProps.isCollapsed}
            isOpen={isOpen}
            ref={tocWrapperRef}
        >
            <NavigationImpression name="toc_navigation">
                <TableOfContents
                    sections={sections}
                    title={t("In this page", { ns: "navigation" })}
                    openStatus={setIsOpen}
                    openLabel={t("Open table of contents", { ns: "common" })}
                    closeLabel={t("Close table of contents", { ns: "common" })}
                />
            </NavigationImpression>
        </TableOfContentsWrapper>
    );
};
